import { useState, useCallback } from 'react';
import { MaintenanceRecordCreate } from '../../../../../types/api';
import {
  useUploadMaintenanceAttachmentMutation,
  useCreateMaintenanceRecordMutation,
} from '../../maintenance.mutations';

export const useMaintenanceManagement = (droneSerial?: string) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // File management state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // STD-016: uploads + record creation go through mutations. The create
  // mutation's onSuccess invalidates the history/upcoming keys (replacing the
  // old `maintenance-record-created` window event); TanStack auto-refetches.
  const uploadAttachmentMutation = useUploadMaintenanceAttachmentMutation();
  const createRecordMutation = useCreateMaintenanceRecordMutation();

  // Combined busy flag: true while uploading files or while creating the record.
  const uploadingFiles = uploadAttachmentMutation.isPending;
  const loading = uploadAttachmentMutation.isPending || createRecordMutation.isPending;

  // Clears the file-input DOM node so re-selecting the same file fires change.
  const clearFileInput = useCallback(() => {
    const fileInput = document.getElementById(
      'maintenance-attachment-upload'
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = '';
  }, []);

  const handleCreateMaintenance = useCallback(
    async (record: MaintenanceRecordCreate) => {
      if (!droneSerial) {
        setError('No drone selected');
        return;
      }

      try {
        setError(null);

        // Upload files first if any are selected.
        // Only the S3 key is persisted; the download URL is fetched fresh on click
        // (presigned URLs expire after 1 hour).
        const attachments: Array<{ name: string; key: string; type?: string }> = [];
        if (selectedFiles.length > 0) {
          for (const file of selectedFiles) {
            try {
              const uploadedFile = await uploadAttachmentMutation.mutateAsync({
                file,
                droneSerial,
              });
              attachments.push({
                name: file.name,
                key: uploadedFile.key,
                type: file.type,
              });
            } catch (uploadErr) {
              console.error('Error uploading file:', uploadErr);
              setError(`Failed to upload file: ${file.name}`);
              setSelectedFiles([]);
              clearFileInput();

              // Rethrow so the dialog's await rejects and shows the error toast
              // instead of declaring success when no record was created.
              throw uploadErr;
            }
          }
        }

        // Ensure drone_serial is set and add attachments
        const recordWithAttachments = {
          ...record,
          drone_serial: droneSerial,
          attachments,
        };

        await createRecordMutation.mutateAsync(recordWithAttachments);

        // Success - close dialog and reset file state. Cache invalidation (and
        // the resulting refetch of the upcoming/history lists) is handled in the
        // create mutation's onSuccess.
        setCreateDialogOpen(false);
        setSelectedFiles([]);
        clearFileInput();
      } catch (err: unknown) {
        console.error('Failed to create maintenance record:', err);

        // Extract error message
        let errorMessage = 'Failed to create maintenance record';
        if (err && typeof err === 'object') {
          if (
            'response' in err &&
            err.response &&
            typeof err.response === 'object' &&
            'data' in err.response
          ) {
            const data = err.response.data as { detail?: string };
            if (data.detail) {
              errorMessage = data.detail;
            }
          } else if ('message' in err && typeof err.message === 'string') {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);
        // Rethrow so the caller's await rejects — the dialog uses this to
        // decide whether to show the success toast vs. the error toast. The
        // local setError() still drives the in-dialog Alert.
        throw err;
      }
    },
    [droneSerial, selectedFiles, uploadAttachmentMutation, createRecordMutation, clearFileInput]
  );

  const handleOpenCreateDialog = useCallback(() => {
    setError(null);
    setCreateDialogOpen(true);
  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
    setError(null);
    setSelectedFiles([]);
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Validate file types and sizes
    const validFiles = files.filter((file) => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
        'text/plain',
        'text/csv',
        'application/json',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        setError(`File type ${file.type} is not supported for ${file.name}.`);
        return false;
      }

      return true;
    });

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return {
    // Dialog state
    createDialogOpen,
    setCreateDialogOpen,

    // Loading and error state
    loading,
    error,

    // File state
    selectedFiles,
    uploadingFiles,

    // Actions
    handleCreateMaintenance,
    handleOpenCreateDialog,
    handleCloseCreateDialog,
    handleFileSelect,
    handleRemoveFile,
  };
};
